import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { chartHelper } from '@builttocreate/engine-utils';
import {
  VictoryChart,
  VictoryLine,
  VictoryTheme,
  VictoryLabel,
  VictoryScatter,
} from 'victory';

import DefaultStyles from '../../../../constants/DefaultFormStyles';
import { chartColors } from '../../../../constants/UtilityColors';

const { getDefaultLine } = chartHelper;

const StyledWrapper = styled.div`
  background-color: ${(props) => props.backgroundColor || DefaultStyles.backgroundColor};
  border: ${(props) => props.border};
  border-radius: ${(props) => (props.borderRadius !== undefined ? `${props.borderRadius}px` : DefaultStyles.borderRadius)};
`;

class Chart extends PureComponent {

  static propTypes = {

    id: PropTypes.string,
    yTitle: PropTypes.string,
    yMin: PropTypes.number,
    yMax: PropTypes.number,
    xTitle: PropTypes.string,
    xMin: PropTypes.number,
    xMax: PropTypes.number,
    lines: PropTypes.arrayOf(PropTypes.shape({
      _id: PropTypes.string,
      title: PropTypes.string,
      description: PropTypes.string,
      points: PropTypes.array //[{_id: String, y: Number, x: Number}, ...]
    })),
    backgroundColor: PropTypes.string,
    borderColor: PropTypes.string,
    borderRadius: PropTypes.number,
    borderWidth: PropTypes.number,

    primaryMaxHeight: PropTypes.number,

  }

  static defaultProps = {
    yTitle: 'Vertical',
    yMin: 0,
    yMax: 100,
    xTitle: 'Horizontal',
    xMin: 0,
    xMax: 100,
    primaryMaxHeight: 280,

    /**
    * Why do we add two points by default to the new line?
    *
    * There is bug with victory-native that will throw an error if
    * there aren't two data points in the array.
    */
    lines: [getDefaultLine()],

  }

  // JS function to calculate the width of the largest label in the y-axis
  calculateMaxLabelWidth = (yMax) => {
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    return Math.ceil(context.measureText(yMax).width);
  }

  render() {

    const {
      id,
      yTitle,
      yMin,
      yMax,
      xTitle,
      xMin,
      xMax,
      backgroundColor,
      borderColor,
      borderRadius,
      borderWidth,
      primaryMaxHeight,
    } = this.props;

    const maxLabelWidth = this.calculateMaxLabelWidth(yMax);
    const dynamicPadding = {
      top: 50,
      left: maxLabelWidth + 60,
      right: 50,
      bottom: 50,
    };

    /**
     * We join width and color to a single border style because chrome displays
     * the border as a two tone color if you use border-color and border-width
     * indvidually.
     */
    const bWidth = borderWidth !== undefined ? `${borderWidth}px` : DefaultStyles.borderWidth;
    const bColor = borderColor || 'transparent';
    const border = `${bWidth} solid ${bColor}`;

    return (
      <StyledWrapper data-testid={`${id}-chart`} {...{ backgroundColor, border, borderRadius }}>
        <VictoryChart
          testID={`${id}-chart-chart`}
          height={primaryMaxHeight}
          width={600}
          domain={{ x: [xMin, xMax], y: [yMin, yMax] }}
          theme={VictoryTheme.material}
          style={{ border: '1px solid black' }}
          padding={dynamicPadding}
        >
          {this.getChartLines()}
          {this.getChartLinePoints()}
          <VictoryLabel
            testID={`${id}-chart-label`}
            x={12}
            y={primaryMaxHeight / 2}
            text={this.transformTitle(yTitle, 50)}
            textAnchor="middle"
            labelPlacement="vertical"
            verticalAnchor="middle"
            angle={-90}
            style={{ fontSize: '10px' }}
          />
          <VictoryLabel
            testID={`${id}-chart-label`}
            x={600 / 2}
            y={primaryMaxHeight - 14}
            text={this.transformTitle(xTitle, 50)}
            textAnchor="middle"
            verticalAnchor="middle"
            labelPlacement="vertical"
            style={{ fontSize: '10px' }}
          />
        </VictoryChart>
      </StyledWrapper>
    );
  }

  transformTitle = (title, splitIndex) => {

    if (!title || title.length <= splitIndex) {
      return title;
    }

    const words = title.split(' ');
    const lines = [];
    let currentLine = '';

    for (const word of words) {

      if ((currentLine + word).length <= splitIndex) {
        
        currentLine += (currentLine ? ' ' : '') + word;

      } else {

        if (currentLine) {
        
          lines.push(currentLine);
        
          currentLine = word;
        
        } else {
          // If a single word is longer than splitIndex, we have to break it
          lines.push(word);
        }
      }

    }

    if (currentLine) {
      lines.push(currentLine);
    }

    return lines.join('\n');
  }

  getChartLines = () => {

    const { id } = this.props;

    return this.getLines().map((line, i) => {

      const nextPoints = line.points.map((point) => {
        return { ...point, label: this.transformTitle(point.label, 80) };
      });

      return (
        <VictoryLine
          testID={`${id}-chart-line-${i}`}
          interpolation="natural"
          key={i}
          data={nextPoints}
          labelComponent={<VictoryLabel renderInPortal dy={-10} />}
          style={{
            data: { stroke: chartColors[i] },
            labels: { fontSize: '10px' }
          }}

        />
      );
    });

  }

  getChartLinePoints = () => {

    const { id } = this.props;


    return this.getLines().map((line, i) => {

    const isSinglePoint = line.points.length === 1;
    const scatterPoints = line.points.map(p => ({
      ...p,
      label: isSinglePoint ? this.transformTitle(p.label, 80) : undefined,
    }));

      return (
        <VictoryScatter
          testID={`${id}-chart-scatter-${i}`}
          key={i}
          data={scatterPoints}
          labelComponent={isSinglePoint ? <VictoryLabel renderInPortal dy={-10} /> : undefined}
          style={{
            data: { fill: chartColors[i] },
            labels: { fontSize: '10px' }
          }}
        />
      );
    });

  }

  getLines = () => {
    const { lines } = this.props;
    return lines.filter((line) => !line.deleted);
  }

}

export default Chart;
